/* global trapFocus debounce removeTrapFocus */

class ShopHeader extends HTMLElement {
  constructor() {
    super()
    this.items = []
    this.popups = []
    this.overlay = null
    this.mobileOverlay = null
    this.mainMenu = null
    this.drawerOpener = null
    this.drawerCloser = null
    this.productSplide = null
    this.accountOpener = null
    this.accountPopup = null
    this.submenuIcons = null
    this.l1LeaveAt = 0
    this.l2HoverTimers = new Map()
    this.L1_TO_L2_DELAY = 200
    this.handleResize = this.handleResize.bind(this)
    this.wasMobile = null
    this.drawerActiveElement = null
    this._drawerKeyupHandler = null
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.header-main-menu-list-item'))
    this.popups = Array.from(this.querySelectorAll('.main-menu-popup-container'))
    this.overlay = document.querySelector('.header-menu-overlay')
    this.mobileOverlay = this.querySelector('.header-menu-mobile-overlay')
    this.mainMenu = this.querySelector('.header-main-menu')
    this.drawerOpener = this.querySelector('.header-main-menu-drawer-opener')
    this.drawerCloser = this.querySelector('.header-menu-drawer-header-close')
    this.productSplide = this.querySelectorAll('.mega-menu-l3-products-splide')
    this.accountOpener = this.querySelector('#account-opener')
    this.accountPopup = this.querySelector('.account-popup-wrapper')
    this.submenuIcons = this.querySelectorAll('.header-submenu-icon-item')
    this.initA11y()
    this.bindEvents()
    this.bindMegaMenuEvents()
    this.wasMobile = this.isMobile()
    window.addEventListener('resize', debounce(this.handleResize, 150))
  }

  initA11y() {
    // Reflect initial visibility state for assistive tech
    this.popups.forEach(popup => {
      popup.setAttribute('aria-hidden', popup.classList.contains('open') ? 'false' : 'true')
      if (!popup.hasAttribute('tabindex')) popup.setAttribute('tabindex', '-1')
    })
    if (this.accountPopup) {
      this.accountPopup.setAttribute('aria-hidden', this.accountPopup.classList.contains('open') ? 'false' : 'true')
      if (!this.accountPopup.hasAttribute('tabindex')) this.accountPopup.setAttribute('tabindex', '-1')
    }
    if (this.mainMenu && this.drawerOpener) {
      const isDrawerOpen = this.mainMenu.classList.contains('mobile-open')
      this.drawerOpener.setAttribute('aria-expanded', isDrawerOpen ? 'true' : 'false')
      if (this.isMobile()) this.mainMenu.setAttribute('aria-hidden', isDrawerOpen ? 'false' : 'true')
      else this.mainMenu.removeAttribute('aria-hidden')
    }
  }

  isActivationKey(event) {
    return event.code === 'Enter' || event.code === 'Space' || event.key === 'Enter' || event.key === ' '
  }

  getFocusableElements(container) {
    if (!container) return []
    const selectors = [
      'a[href]:not([tabindex="-1"])',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ]
    return Array.from(container.querySelectorAll(selectors.join(','))).filter(el => {
      if (!(el instanceof HTMLElement)) return false
      if (el.hasAttribute('disabled')) return false
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
    })
  }

  focusFirstIn(container) {
    const focusables = this.getFocusableElements(container)
    const target = focusables[0] || container
    if (target && target.focus) target.focus({ preventScroll: true })
  }

  setPopupHidden(popup, hidden) {
    if (!popup) return
    popup.setAttribute('aria-hidden', hidden ? 'true' : 'false')
  }

  syncMegaMenuAria() {
    if (this.megaMenuLevel1Items?.length) {
      this.megaMenuLevel1Items.forEach(el => {
        if (el.getAttribute('aria-expanded') === null) return
        el.setAttribute('aria-expanded', el.classList.contains('open') ? 'true' : 'false')
      })
    }
    if (this.megaMenuLevel2Items?.length) {
      this.megaMenuLevel2Items.forEach(el => {
        if (el.getAttribute('aria-expanded') === null) return
        const expanded = el.classList.contains('open') || el.classList.contains('mobile-open')
        el.setAttribute('aria-expanded', expanded ? 'true' : 'false')
      })
    }
  }

  getPopupById(id) {
    return this.popups.find(popup => popup.dataset.popupId === id || popup.id === id)
  }

  isMobile() {
    return window.matchMedia('(max-width: 1024px)').matches
  }

  closeAll() {
    this.items.forEach(item => {
      item.classList.remove('open')
      if (item.getAttribute('aria-expanded') !== null) item.setAttribute('aria-expanded', 'false')
    })
    this.popups.forEach(popup => {
      popup.classList.remove('open')
      popup.style.removeProperty('left')
      this.setPopupHidden(popup, true)
    })
    if (this.overlay) this.overlay.classList.remove('open')
  }

  openForItem(item, options = {}) {
    const { id } = item.dataset
    if (!id) return
    const popup = this.getPopupById(id)
    if (!popup) {
      this.closeAll()
      return
    }
    const headerRect = this.getBoundingClientRect()
    this.closeAll()
    item.classList.add('open')
    item.setAttribute('aria-expanded', 'true')
    popup.classList.add('open')
    this.setPopupHidden(popup, false)
    if (popup.classList.contains('main-menu-popup')) {
      const itemRect = item.getBoundingClientRect()
      const popupRect = popup.getBoundingClientRect()
      const left = itemRect.left - headerRect.left + (itemRect.width - popupRect.width) / 2
      popup.style.left = `${left}px`
    }
    if (this.overlay && popup.classList.contains('main-menu-full-screen-popup')) {
      this.overlay.classList.add('open')
    }
    if (options.focusPopup) this.focusFirstIn(popup)
  }

  bindEvents() {
    // Drawer open/close (mobile)
    if (this.drawerOpener && this.mainMenu) {
      this.drawerOpener.addEventListener('click', () => this.openDrawer({ triggeredBy: this.drawerOpener }))
      this.drawerOpener.addEventListener('keydown', event => {
        if (!this.isActivationKey(event)) return
        event.preventDefault()
        this.openDrawer({ focusFirst: true, triggeredBy: this.drawerOpener })
      })
    }
    if (this.drawerCloser && this.mainMenu) {
      this.drawerCloser.addEventListener('click', () => this.closeDrawer())
    }
    // Close drawer even if there are multiple closers
    this.addEventListener('click', event => {
      const closer = event.target.closest('.header-menu-drawer-header-close')
      if (!closer) return
      event.preventDefault()
      this.closeDrawer()
      this.clearMobilePopups()
      this.resetMobileMegaMenuState()
      const countryDrawer = document.querySelector('.country-selector-drawer')
      if (countryDrawer) countryDrawer.close()
    })

    this.items.forEach(item => {
      item.addEventListener('mouseenter', ev => {
        if (this.isMobile()) {
          ev.preventDefault()
          return
        }
        this.openForItem(item)
      })
      item.addEventListener('focusin', () => {
        if (this.isMobile()) return
        this.openForItem(item)
      })
      item.addEventListener('click', ev => {
        if (!this.isMobile()) return
        const { id } = item.dataset
        const popup = id ? this.getPopupById(id) : null
        if (!popup) return
        ev.preventDefault()
        this.clearMobilePopups()
        popup.classList.add('mobile-open')
        this.setPopupHidden(popup, false)
      })
      item.addEventListener('keydown', event => {
        if (event.code === 'Escape') {
          event.preventDefault()
          this.closeAll()
          return
        }
        if (this.isMobile()) {
          if (!this.isActivationKey(event)) return
          event.preventDefault()
          item.click()
          const { id } = item.dataset
          const popup = id ? this.getPopupById(id) : null
          if (popup) this.focusFirstIn(popup)
          return
        }
        if (event.code === 'ArrowRight' || event.code === 'ArrowLeft') {
          event.preventDefault()
          this.moveTopLevelFocus(item, event.code === 'ArrowRight' ? 1 : -1)
          return
        }
        if (!this.isActivationKey(event)) return
        const { id } = item.dataset
        const popup = id ? this.getPopupById(id) : null
        if (!popup) return
        event.preventDefault()
        this.openForItem(item, { focusPopup: true })
      })
      item.addEventListener('mouseleave', event => {
        const related = event.relatedTarget
        const { id } = item.dataset
        const popup = id ? this.getPopupById(id) : null
        if (popup && related && popup.contains(related)) return
        this.closeAll()
      })
    })

    this.popups.forEach(popup => {
      popup.addEventListener('mouseleave', event => {
        const related = event.relatedTarget
        const item = this.items.find(i => i.dataset.id === popup.dataset.popupId || i.dataset.id === popup.id)
        if (item && related && item.contains(related)) return
        this.closeAll()
        if (popup.classList.contains('mega-menu')) {
          this.resetMegaMenuToFirst()
        }
      })
    })

    if (this.overlay) {
      this.overlay.addEventListener('mouseenter', () => this.closeAll())
    }

    if (this.accountOpener && this.accountPopup) {
      // Hover-only for desktop account popup
      this.accountOpener.addEventListener('mouseenter', () => {
        if (this.isMobile()) return
        this.openAccountPopup()
      })
      this.accountOpener.addEventListener('mouseleave', event => {
        const related = event.relatedTarget
        if (related && this.accountPopup.contains(related)) return
        this.closeAccountPopup()
      })
      this.accountPopup.addEventListener('mouseenter', () => {
        if (this.isMobile()) return
        this.openAccountPopup()
      })
      this.accountPopup.addEventListener('mouseleave', event => {
        const related = event.relatedTarget
        if (related && this.accountOpener.contains(related)) return
        this.closeAccountPopup()
      })

      // Keyboard + focus support (desktop)
      const maybeCloseAccount = event => {
        if (this.isMobile()) return
        const related = event.relatedTarget
        if (related && (this.accountOpener.contains(related) || this.accountPopup.contains(related))) return
        this.closeAccountPopup()
      }
      this.accountOpener.addEventListener('focusin', () => {
        if (this.isMobile()) return
        this.openAccountPopup()
      })
      this.accountPopup.addEventListener('focusin', () => {
        if (this.isMobile()) return
        this.openAccountPopup()
      })
      this.accountOpener.addEventListener('focusout', maybeCloseAccount)
      this.accountPopup.addEventListener('focusout', maybeCloseAccount)
      this.accountOpener.addEventListener('keydown', event => {
        if (event.code === 'Escape') {
          event.preventDefault()
          this.closeAccountPopup()
          this.accountOpener.focus({ preventScroll: true })
          return
        }
        if (this.isMobile()) return
        if (!this.isActivationKey(event)) return
        event.preventDefault()
        this.openAccountPopup()
        this.focusFirstIn(this.accountPopup)
      })
      this.accountPopup.addEventListener('keydown', event => {
        if (event.code !== 'Escape') return
        event.preventDefault()
        this.closeAccountPopup()
        this.accountOpener.focus({ preventScroll: true })
      })
    }

    this.submenuIcons.forEach(el => {
      // Mobile: toggle submenu icons
      el.addEventListener('click', () => {
        if (!this.isMobile()) return
        const isOpen = el.classList.contains('mobile-open')
        this.submenuIcons.forEach(el => {
          el.classList.remove('mobile-open')
          el.style.height = ''
          el.setAttribute('aria-expanded', 'false')
        })
        if (isOpen) return
        el.classList.add('mobile-open')
        el.style.height = `${el.scrollHeight}px`
        el.setAttribute('aria-expanded', 'true')
      })
      el.addEventListener('keydown', event => {
        if (!this.isMobile()) return
        if (!this.isActivationKey(event)) return
        event.preventDefault()
        el.click()
      })
    })
  }

  openDrawer(options = {}) {
    this.classList.add('mobile-open')
    const { triggeredBy = null, focusFirst = false } = options
    if (triggeredBy) this.drawerActiveElement = triggeredBy
    this.mainMenu.classList.add('mobile-open')
    if (this.mobileOverlay) this.mobileOverlay.classList.add('mobile-open')
    if (this.drawerOpener) this.drawerOpener.setAttribute('aria-expanded', 'true')
    if (this.mainMenu) this.mainMenu.setAttribute('aria-hidden', 'false')
    if (typeof trapFocus === 'function') trapFocus(this.mainMenu)
    if (!this._drawerKeyupHandler) {
      this._drawerKeyupHandler = event => {
        if (event.code !== 'Escape') return
        event.preventDefault()
        this.closeDrawer()
        this.clearMobilePopups()
        this.resetMobileMegaMenuState()
        const countryDrawer = document.querySelector('.country-selector-drawer')
        if (countryDrawer) countryDrawer.close()
      }
    }
    this.mainMenu.addEventListener('keyup', this._drawerKeyupHandler)
    if (focusFirst) this.focusFirstIn(this.mainMenu)
  }

  closeDrawer() {
    this.classList.remove('mobile-open')
    this.mainMenu.classList.remove('mobile-open')
    if (this.mobileOverlay) this.mobileOverlay.classList.remove('mobile-open')
    this.querySelectorAll('.mobile-open').forEach(el => el.classList.remove('mobile-open'))
    this.submenuIcons.forEach(el => el.style.removeProperty('height'))
    if (this.drawerOpener) this.drawerOpener.setAttribute('aria-expanded', 'false')
    if (this.isMobile() && this.mainMenu) this.mainMenu.setAttribute('aria-hidden', 'true')
    if (this._drawerKeyupHandler && this.mainMenu) this.mainMenu.removeEventListener('keyup', this._drawerKeyupHandler)
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.drawerActiveElement || this.drawerOpener)
  }

  handleResize() {
    const isMobileNow = this.isMobile()
    if (this.wasMobile && !isMobileNow) {
      this.classList.remove('mobile-open')
      if (this.mainMenu) this.mainMenu.classList.remove('mobile-open')
      if (this.mobileOverlay) this.mobileOverlay.classList.remove('mobile-open')
      if (this.drawerOpener) this.drawerOpener.setAttribute('aria-expanded', 'false')
      if (this.mainMenu) this.mainMenu.removeAttribute('aria-hidden')
    }
    this.wasMobile = isMobileNow
  }

  openAccountPopup() {
    this.accountOpener.classList.add('active')
    this.accountOpener.setAttribute('aria-expanded', 'true')
    this.accountPopup.classList.add('open')
    this.accountPopup.setAttribute('aria-hidden', 'false')
    if (this.overlay) this.overlay.classList.add('open')
  }

  closeAccountPopup() {
    this.accountOpener.classList.remove('active')
    this.accountOpener.setAttribute('aria-expanded', 'false')
    this.accountPopup.classList.remove('open')
    this.accountPopup.setAttribute('aria-hidden', 'true')
    if (this.overlay) this.overlay.classList.remove('open')
  }

  moveTopLevelFocus(currentItem, offset) {
    const index = this.items.indexOf(currentItem)
    if (index < 0) return
    const nextIndex = (index + offset + this.items.length) % this.items.length
    const nextItem = this.items[nextIndex]
    const focusTarget = nextItem.querySelector('a[role="menuitem"]') || nextItem
    if (focusTarget && focusTarget.focus) focusTarget.focus({ preventScroll: true })
    if (!this.isMobile()) this.openForItem(nextItem)
  }

  bindMegaMenuEvents() {
    this.megaMenuLevel1Items = Array.from(this.querySelectorAll('.mega-menu-level-1-item'))
    this.megaMenuLevel2Containers = Array.from(this.querySelectorAll('.mega-menu-level-2'))
    this.megaMenuLevel2Items = Array.from(this.querySelectorAll('.mega-menu-level-2-item'))
    this.megaMenuLevel3Contents = Array.from(this.querySelectorAll('.mega-menu-level-3-content'))
    this.megaMenuLevel2Links = Array.from(this.querySelectorAll('.mega-menu-level-2-links'))
    this.megaMenuBackButtons = Array.from(this.querySelectorAll('.mega-menu-back-button'))
    this.megaMenuLevel2BackButtons = Array.from(this.querySelectorAll('.mega-menu-level-2-back-button'))

    if (!this.megaMenuLevel1Items.length || !this.megaMenuLevel2Containers.length) return

    this.megaMenuLevel1Items.forEach(item => {
      item.addEventListener('mouseenter', ev => {
        if (this.isMobile()) {
          ev.preventDefault()
          return
        }
        // Reset L1 leave timestamp to prevent delayed L2 activation
        this.l1LeaveAt = 0
        this.openMegaMenuLevel1(item)
      })
      item.addEventListener('focusin', () => {
        if (this.isMobile()) return
        this.l1LeaveAt = 0
        this.openMegaMenuLevel1(item)
      })
      item.addEventListener('mouseleave', () => {
        if (this.isMobile()) return
        // Mark L1 leave time to debounce accidental L2 activation
        this.l1LeaveAt = Date.now()
      })
      item.addEventListener('click', ev => {
        if (!this.isMobile()) return
        const { level1Id } = item.dataset
        if (!level1Id) return
        ev.preventDefault()
        this.megaMenuLevel2Containers.forEach(el => el.classList.remove('mobile-open'))
        const level2Container = this.megaMenuLevel2Containers.find(el => el.dataset.level1Id === level1Id)
        if (level2Container) level2Container.classList.add('mobile-open')
        this.syncMegaMenuAria()
      })
      item.addEventListener('keydown', event => {
        if (!this.isActivationKey(event)) return
        event.preventDefault()
        if (this.isMobile()) item.click()
        else this.openMegaMenuLevel1(item)
      })
    })

    this.megaMenuLevel2Items.forEach(item => {
      item.addEventListener('mouseenter', ev => {
        if (this.isMobile()) {
          ev.preventDefault()
          return
        }
        if (this.shouldDelayL2Activation()) {
          this.cancelL2Hover(item)
          const timer = setTimeout(() => {
            this.openMegaMenuLevel2(item)
            this.l2HoverTimers.delete(item)
          }, this.L1_TO_L2_DELAY)
          this.l2HoverTimers.set(item, timer)
          return
        }
        this.openMegaMenuLevel2(item)
      })
      item.addEventListener('focusin', () => {
        if (this.isMobile()) return
        this.cancelL2Hover(item)
        this.openMegaMenuLevel2(item)
      })
      item.addEventListener('mouseleave', () => {
        if (this.isMobile()) return
        this.cancelL2Hover(item)
      })
      item.addEventListener('click', ev => {
        if (!this.isMobile()) return
        ev.preventDefault()

        const { level2Id } = item.dataset
        if (!level2Id) return
        const level3Content = this.megaMenuLevel3Contents.find(el => el.dataset.level2Id === level2Id)
        if (!level3Content) return
        const isOpen = level3Content.classList.contains('mobile-open')
        this.megaMenuLevel2Items.forEach(el => el.classList.remove('mobile-open'))
        this.clearMobileL3State()
        if (isOpen) return
        item.classList.add('mobile-open')
        this.refreshSplide(level3Content)
        level3Content.classList.add('mobile-open')
        level3Content.style.height = `${level3Content.scrollHeight + 49}px`
        level3Content.style.maxHeight = '5000px'
        level3Content.style.padding = '24px 0'
        this.syncMegaMenuAria()
      })
      item.addEventListener('keydown', event => {
        if (!this.isActivationKey(event)) return
        event.preventDefault()
        if (this.isMobile()) item.click()
        else this.openMegaMenuLevel2(item)
      })
    })

    this.megaMenuLevel2Links.forEach(link => {
      link.addEventListener('click', ev => {
        if (!this.isMobile()) return
        ev.preventDefault()
        const isOpen = link.classList.contains('mobile-open')
        this.megaMenuLevel2Links.forEach(el => {
          el.classList.remove('mobile-open')
          el.style.height = ''
          el.setAttribute('aria-expanded', 'false')
        })
        if (isOpen) return
        link.classList.add('mobile-open')
        link.style.height = `${link.scrollHeight}px`
        link.setAttribute('aria-expanded', 'true')
      })
      link.addEventListener('keydown', event => {
        if (!this.isMobile()) return
        if (!this.isActivationKey(event)) return
        event.preventDefault()
        link.click()
      })
    })

    this.megaMenuBackButtons.forEach(button => {
      button.addEventListener('click', ev => {
        if (!this.isMobile()) return
        ev.preventDefault()
        const popup = button.closest('.main-menu-popup-container')
        if (popup) {
          popup.classList.remove('mobile-open')
          this.setPopupHidden(popup, true)
        }
        this.megaMenuLevel2Containers.forEach(el => el.classList.remove('mobile-open'))
        this.syncMegaMenuAria()
      })
    })

    this.megaMenuLevel2BackButtons.forEach(button => {
      button.addEventListener('click', ev => {
        if (!this.isMobile()) return
        ev.preventDefault()
        const level2Container = button.closest('.mega-menu-level-2')
        if (!level2Container) return
        level2Container.classList.remove('mobile-open')
        level2Container.querySelectorAll('.mega-menu-level-2-item').forEach(el => el.classList.remove('open'))
        this.megaMenuLevel3Contents.forEach(el => el.classList.remove('open'))
        this.syncMegaMenuAria()
      })
    })
  }

  shouldDelayL2Activation() {
    if (!this.l1LeaveAt) return false
    return Date.now() - this.l1LeaveAt < this.L1_TO_L2_DELAY
  }

  cancelL2Hover(item) {
    const timer = this.l2HoverTimers.get(item)
    if (!timer) return
    clearTimeout(timer)
    this.l2HoverTimers.delete(item)
  }

  clearMobilePopups() {
    this.popups.forEach(el => {
      el.classList.remove('mobile-open')
      this.setPopupHidden(el, true)
    })
  }

  clearMobileL3State() {
    this.megaMenuLevel3Contents.forEach(el => {
      el.classList.remove('mobile-open')
      el.style.height = ''
      el.style.padding = ''
    })
  }

  resetMobileMegaMenuState() {
    this.megaMenuLevel2Containers?.forEach(el => el.classList.remove('mobile-open'))
    this.clearMobileL3State()
  }

  refreshSplide(container) {
    const productList = container.querySelectorAll('.splide')
    productList.forEach(el => el.slider?.refresh())
  }

  openMegaMenuLevel1(item) {
    const { level1Id } = item.dataset
    if (!level1Id) return

    this.megaMenuLevel1Items.forEach(el => el.classList.remove('open'))
    this.megaMenuLevel2Containers.forEach(el => el.classList.remove('open'))
    this.megaMenuLevel2Items.forEach(el => el.classList.remove('open'))
    this.megaMenuLevel3Contents.forEach(el => el.classList.remove('open'))

    item.classList.add('open')
    const level2Container = this.megaMenuLevel2Containers.find(el => el.dataset.level1Id === level1Id)
    if (!level2Container) return
    level2Container.classList.add('open')

    const firstLevel2Item = level2Container.querySelector('.mega-menu-level-2-item')
    if (!firstLevel2Item) return
    firstLevel2Item.classList.add('open')

    const { level2Id } = firstLevel2Item.dataset
    if (!level2Id) return
    const level3Content = this.megaMenuLevel3Contents.find(el => el.dataset.level2Id === level2Id)
    if (level3Content) level3Content.classList.add('open')
    this.syncMegaMenuAria()
  }

  openMegaMenuLevel2(item) {
    const { level2Id } = item.dataset
    if (!level2Id) return

    this.megaMenuLevel2Items.forEach(el => el.classList.remove('open'))
    this.megaMenuLevel3Contents.forEach(el => el.classList.remove('open'))

    item.classList.add('open')
    const level3Content = this.megaMenuLevel3Contents.find(el => el.dataset.level2Id === level2Id)
    if (level3Content) level3Content.classList.add('open')
    this.syncMegaMenuAria()
  }

  resetMegaMenuToFirst() {
    if (!this.megaMenuLevel1Items?.length || !this.megaMenuLevel2Containers?.length) return
    this.megaMenuLevel1Items.forEach(el => el.classList.remove('open'))
    this.megaMenuLevel2Containers.forEach(el => el.classList.remove('open'))
    this.megaMenuLevel2Items?.forEach(el => el.classList.remove('open'))
    this.megaMenuLevel3Contents?.forEach(el => el.classList.remove('open'))

    const firstL1 = this.megaMenuLevel1Items[0]
    if (!firstL1) return
    firstL1.classList.add('open')
    const level2Container = this.megaMenuLevel2Containers.find(el => el.dataset.level1Id === firstL1.dataset.level1Id)
    if (!level2Container) return
    level2Container.classList.add('open')
    const firstL2 = level2Container.querySelector('.mega-menu-level-2-item')
    if (!firstL2) return
    firstL2.classList.add('open')
    const { level2Id } = firstL2.dataset
    if (!level2Id) return
    const level3Content = this.megaMenuLevel3Contents.find(el => el.dataset.level2Id === level2Id)
    if (level3Content) level3Content.classList.add('open')
    this.syncMegaMenuAria()
  }
}

if (!customElements.get('shop-header')) {
  customElements.define('shop-header', ShopHeader)
}

class CountryDrawerOpener extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    this.bindCountrySelectorEvents()
  }

  bindCountrySelectorEvents() {
    const drawer = document.querySelector('#CountrySelectorDrawer')
    if (!drawer) return
    this.addEventListener('click', () => {
      if (this.dataset.isOuter) drawer.open(this, true)
      else drawer.open(this)
      this.setAttribute('aria-expanded', 'true')
    })
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          this.setAttribute('aria-expanded', drawer.classList.contains('active') ? 'true' : 'false')
        }
      })
    })
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] })
  }
}

if (!customElements.get('country-drawer-opener')) {
  customElements.define('country-drawer-opener', CountryDrawerOpener)
}

// ========== Country Selector Drawer ==========

class CountrySelectorDrawer extends HTMLElement {
  constructor() {
    super()
    this.activeElement = null
    this.overlay = this.querySelector('.country-selector-overlay')
    this.closeButton = this.querySelectorAll('.country-selector-drawer__close')
    this.form = this.querySelector('.country-selector-form')
    this.countryInput = this.querySelector('#CountryCodeInput')
    this.countryItems = this.querySelectorAll('.country-selector-item')
    this.outer = false
    this.langCodes = ['es', 'fr', 'it', 'ro', 'pl', 'da', 'pt', 'sv', 'fi', 'el', 'ga', 'nl', 'bg']

    if (this.overlay) this.overlay.addEventListener('click', () => this.close())
    if (this.closeButton) {
      this.closeButton.forEach(btn => btn.addEventListener('click', () => this.close()))
    }
    this.addEventListener('keyup', e => {
      if (e.code === 'Escape') this.close()
    })
    this.countryItems.forEach(item => {
      item.addEventListener('click', e => this.countryItemClick(e, item))
    })
  }

  countryItemClick(e, item) {
    e.preventDefault()
    const currentUrl = new URL(window.location.href)
    const itemUrl = new URL(item.href)
    const regex = new RegExp(`/(?:${this.langCodes.join('|')})(?=/|$)`)
    const currentMatch = currentUrl.pathname.match(regex)
    const itemMatch = itemUrl.pathname.match(regex)
    const currentLang = currentMatch ? currentMatch[0].slice(1) : null
    const itemLang = itemMatch ? itemMatch[0].slice(1) : null
    if (currentUrl.origin === itemUrl.origin && currentLang === itemLang) return

    let res = item.href
    let path = currentUrl.pathname
    if (currentUrl.origin === itemUrl.origin) {
      if (currentLang) path = path.replace(`/${currentLang}/`, '')
      res = item.href + path + currentUrl.search + currentUrl.hash
    }
    window.location.href = res
    this.close()
  }

  open(triggeredBy, isOuter = false) {
    if (triggeredBy) this.activeElement = triggeredBy
    this.outer = isOuter
    this.classList.add(isOuter ? 'country-selector-drawer--outer' : 'active')
    trapFocus(this)
  }

  close() {
    if (this.outer) this.classList.remove('country-selector-drawer--outer')
    this.classList.remove('active')
    removeTrapFocus(this.activeElement)
  }

  selectCountry(countryCode) {
    if (!this.countryInput || !this.form) return
    this.countryInput.value = countryCode
    this.form.submit()
  }
}

if (!customElements.get('country-selector-drawer')) {
  customElements.define('country-selector-drawer', CountrySelectorDrawer)
}
